import { MapListingGrid } from '@src/components/GridList';
import styles from './MapListingContainer.module.scss';
import Map from '@src/components/Map';
import { getLastSegmentFromURL } from '@src/utils/url';
import React, { useState, useEffect, useRef } from 'react';
import Heading from '@src/elements/Heading';
import Pagination from '@src/components/Pagination';
import classNames from 'classnames';
import { ButtonStandard } from '@src/components/Button';
import { useRouter } from 'next/router';
import LocationFilters from '@src/components/LocationFilters';
import { PATHS } from '@src/constants/site';
import { XL_BREAKPOINT } from '@src/constants/breakpoints';
import { useViewport } from '@src/hooks/useViewport';
import {
  FlattenedMapCommunityDetail,
  MapLocationOptions
} from '@src/types/map';
import { getFilteredMapData, getLocationName } from '@src/utils/map';
import Head from 'next/head';
import { algoliasearch } from 'algoliasearch';
import { CommunitySearchHit } from '@src/types/algolia';
import CommunitySearchBar from '@src/components/CommunitySearchBar';
import { SectionTitleBlockFragment } from '@src/graphql/gql-types';
import { CONTENTFUL_PAGE_CONTENT_TYPES } from '@src/constants/contentful';
import SectionTitle from '@src/components/SectionTitle';

type MapListingContainerProps = {
  mapData?: {
    transformedData?: FlattenedMapCommunityDetail[];
    communityLocations?: MapLocationOptions;
    locationName?: string;
    filteredData?: FlattenedMapCommunityDetail[];
    listingsData?: FlattenedMapCommunityDetail[];
    locationInfoBlock?: SectionTitleBlockFragment | null;
  };
};

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
  process.env.NEXT_PUBLIC_ALGOLIA_CLIENT_API_KEY || ''
);

const indexName =
  process.env.NEXT_PUBLIC_ALGOLIA_COMMUNITY_SEARCH_INDEX ||
  'levande_community_search_prod';

const MapListingContainer: React.FC<MapListingContainerProps> = ({
  mapData
}) => {
  const { query } = useRouter();
  const { width } = useViewport();

  const [isMapVisible, setIsMapVisible] = useState<boolean | undefined>(
    undefined
  );
  const [filteredMapData, setFilteredMapData] = useState<
    FlattenedMapCommunityDetail[]
  >(mapData?.filteredData || []);
  const [listingsData, setListingsData] = useState<
    FlattenedMapCommunityDetail[]
  >(mapData?.listingsData || []);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const isRoot = getLastSegmentFromURL(router) === PATHS.FIND_YOUR_COMMUNITY;
  const icon = isMapVisible ? 'grid' : 'map';
  const itemsPerPage = 6;
  const numPages = Math.ceil(filteredMapData?.length / itemsPerPage);

  const [locationName, setLocationName] = useState<string>(
    mapData?.locationName || ''
  );

  const [noResults, setNoResults] = useState(false);

  const getNearbyCommunities = async (lat: number, lng: number) => {
    const response = await searchClient.searchSingleIndex({
      indexName: indexName,
      searchParams: {
        aroundLatLng: `${lat},${lng}`,
        aroundRadius: 40000, // 40km
        hitsPerPage: 75,
        facetFilters: ['type:community']
      }
    });
    return response.hits as CommunitySearchHit[];
  };

  useEffect(() => {
    const { location, ...rest } = query;

    if (rest.lat && rest.lng && location === undefined) {
      (async () => {
        if (mapData) {
          const nearbyCommunities = await getNearbyCommunities(
            Number(rest?.lat),
            Number(rest?.lng)
          );

          const { transformedData } = mapData;

          if (!transformedData) {
            return;
          }

          if (nearbyCommunities.length === 0) {
            setFilteredMapData(transformedData);
            const listingsDataChunks = [
              ...chunkArray(transformedData, itemsPerPage)
            ];
            const pageIndex = Number(router?.query?.page || 1) - 1;
            setListingsData(listingsDataChunks[pageIndex]);
            setNoResults(true);
            return;
          }

          const filteredNearbyCommunities = transformedData.filter(
            (community) =>
              nearbyCommunities.some((hit: CommunitySearchHit) => {
                return hit.slug === community.slug;
              })
          );

          setFilteredMapData(filteredNearbyCommunities);
          const listingsDataChunks = [
            ...chunkArray(filteredNearbyCommunities, itemsPerPage)
          ];
          const pageIndex = Number(router?.query?.page || 1) - 1;
          setListingsData(listingsDataChunks[pageIndex]);
          setNoResults(false);
        }
      })();
    }

    if (width > XL_BREAKPOINT && !isMapVisible) {
      setIsMapVisible(true);
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const changePage = (pageNum: number) => {
    router.push(
      {
        query: { ...query, page: pageNum + 1 }
      },
      undefined,
      { shallow: true }
    );
    scrollToRef();
  };

  function* chunkArray(arr: Array<any>, n: number) {
    for (let i = 0; i < arr.length; i += n) {
      yield arr.slice(i, i + n);
    }
  }

  useEffect(() => {
    if (!mapData) {
      return;
    }

    const { communityLocations, transformedData } = mapData;

    const newSelectedLocationArray = router.asPath
      .split('?')[0]
      .split('/')
      .filter(Boolean)
      .slice(1);

    const newSelectedLocation = {
      state: newSelectedLocationArray?.[0] || 'all',
      region: newSelectedLocationArray?.[1] || 'all',
      area: newSelectedLocationArray?.[2] || 'all',
      suburb: newSelectedLocationArray?.[3] || 'all'
    };

    const filteredData = getFilteredMapData(
      transformedData || [],
      newSelectedLocation
    );

    const locationName = getLocationName(
      newSelectedLocation,
      communityLocations
    );

    setLocationName(locationName);
    setFilteredMapData(filteredData);
    const listingsDataChunks = [...chunkArray(filteredData, itemsPerPage)];
    const pageIndex = Number(router?.query?.page || 1) - 1;
    setListingsData(listingsDataChunks[pageIndex]);
  }, [router.asPath, router, mapData]);

  const scrollToRef = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getPageTitle = () => {
    if (!mapData) {
      return 'Find your community | Levande';
    }

    const { communityLocations } = mapData;

    const locationArray = router.asPath
      .split('?')[0]
      .split('/')
      .filter(Boolean)
      .slice(1);

    const location = {
      state: locationArray?.[0] || 'all',
      region: locationArray?.[1] || 'all',
      area: locationArray?.[2] || 'all',
      suburb: locationArray?.[3] || 'all'
    };

    let pageTitle = 'Find your community';

    // max 2 levels shown, e.g. State > Suburb, State > Region, State > Area
    if (location.suburb !== 'all') {
      const suburb =
        communityLocations?.states[location.state]?.regions[location.region]
          ?.areas[location.area]?.suburbs[location.suburb]?.label;
      pageTitle += ` | ${suburb}`;
    }
    if (location.area !== 'all' && location.suburb === 'all') {
      const area =
        communityLocations?.states[location.state]?.regions[location.region]
          ?.areas[location.area]?.label;
      pageTitle += ` | ${area}`;
    }
    if (location.region !== 'all' && location.area === 'all') {
      const region =
        communityLocations?.states[location.state]?.regions[location.region]
          ?.label;
      pageTitle += ` | ${region}`;
    }
    if (location.state !== 'all') {
      const state = communityLocations?.states[location.state]?.label;
      pageTitle += ` | ${state}`;
    }

    if (query?.page && Number(query?.page) > 1) {
      pageTitle += ` | Page ${query?.page}`;
    }

    pageTitle += ` | Levande`;

    return pageTitle;
  };

  const getPageHeading = () => {
    const { location, p: payload } = query;
    if (payload && location === undefined) {
      const { locality, state } = JSON.parse(atob(payload as string));

      if (noResults) {
        return 'Find your Levande community';
      }
      return `Communities near ${locality}, ${state}`;
    }

    if (isRoot) {
      return 'Find your Levande community';
    }
    return `Communities in ${locationName}`;
  };

  const getResultsText = () => {
    const { location, nh, p: payload } = query;

    // invalid query when Let's go button is clicked (e.g. djksdajkfs)
    if (nh === '1') {
      return <p>No communities found</p>;
    }

    // postcode lat/lon search
    if (payload && location === undefined) {
      const { locality, state } = JSON.parse(atob(payload as string));

      // postcode has no results
      if (noResults) {
        return (
          <p>
            No communities found in {locality}, {state}
          </p>
        );
      }
      return (
        <p>
          {filteredMapData?.length > 0 ? filteredMapData?.length : 'No'}
          {filteredMapData?.length > 1 ? ' communities' : ' community'} matching
        </p>
      );
    }
    return (
      <p>
        {filteredMapData?.length > 0 ? filteredMapData?.length : 'No'}
        {filteredMapData?.length > 1 ? ' communities' : ' community'} matching
      </p>
    );
  };

  return (
    <>
      <Head>
        <title>{getPageTitle()}</title>
      </Head>
      <CommunitySearchBar mapListingVariant />

      {mapData && (
        <div
          className={classNames(styles.mapListingWrapper, {
            [styles.mapListingWrapperFull]: !isMapVisible
          })}
          ref={scrollRef}
        >
          <div
            className={classNames(styles.mapListingLeft, {
              [styles.mapListingFullContent]: !isMapVisible
            })}
          >
            <div className={styles.mapListingHeader}>
              <Heading priority={1} fontStyle="h1">
                {getPageHeading()}
              </Heading>
              <div className={styles.infoTextBlock}>
                {getResultsText()}
                <div className={styles.mapToggle}>
                  <ButtonStandard
                    boxIcon={icon}
                    iconPosition="left"
                    fillVariant="outline"
                    onClick={() => {
                      setIsMapVisible(!isMapVisible);
                    }}
                  >
                    {isMapVisible ? 'Grid' : 'Map'}
                  </ButtonStandard>
                </div>
              </div>
              <LocationFilters
                locationOptions={mapData?.communityLocations}
                showAreaFilter
                showSuburbFilter
              />
            </div>
            <div
              className={classNames(styles.mapGridList, {
                [styles.mapGridListHidden]: isMapVisible
              })}
            >
              <MapListingGrid mapData={listingsData} />
            </div>
            <div
              className={classNames(styles.mapListPagination, {
                [styles.mapListPaginationHidden]: isMapVisible
              })}
            >
              {numPages !== 0 && filteredMapData?.length > itemsPerPage && (
                <Pagination
                  boundaryCount={1}
                  defaultPage={Number(router?.query?.page || 1)}
                  numPages={numPages}
                  onPageChanged={(pageNum) => {
                    changePage(pageNum);
                  }}
                  siblingCount={1}
                />
              )}
            </div>
          </div>

          <div
            className={classNames(styles.mapListingRight, {
              [styles.hidden]: !isMapVisible
            })}
          >
            <div className={styles.mapWidget}>
              <Map
                zoomControl={true}
                fullscreenControl={true}
                streetViewControl={true}
                mapTypeControl={true}
                hasPopupCard={true}
                mapData={filteredMapData}
                maxZoom={17}
              />
            </div>
          </div>
        </div>
      )}
      {mapData?.locationInfoBlock && (
        <SectionTitle
          {...(mapData?.locationInfoBlock as SectionTitleBlockFragment)}
          pageType={CONTENTFUL_PAGE_CONTENT_TYPES.NATIONAL_STANDARD}
        />
      )}
    </>
  );
};

export default MapListingContainer;
